<script lang="ts">
import { defineComponent, PropType, inject, ref, computed, onMounted } from 'vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import { useDrawerNavigationStore } from '@/stores/drawerNavigation'
import { useUserStore } from '@/stores/user'
import {
  staticPagesAboutPath,
  staticPagesHelpPath,
  staticPagesTermsPath,
  staticPagesPrivacyPath,
  mypagePath,
  editUserProfilesPath,
  timelinePath,
  pressReleasesPath,
  newUserRegistrationPath,
  newUserSessionPath,
  destroyUserSessionPath,
  themePath,
  documentsPath,
  videosPath,
  eventsPath,
  rankingIndexPath,
} from '@/generated/railsRoutes'

import type { Ref } from 'vue'

export default defineComponent({
  name: 'DrawerNavigation',

  components: {
    ButtonSubmit,
  },

  props: {
    items: {
      type: Array as PropType<ThemeItem[]>,
      required: true,
    },

    keywords: {
      type: Array as PropType<{label: string, to: string}[]>,
      required: true,
    },
  },

  setup(props) {
    // constants
    const EXTERNAL_URL = inject<Record<string, string>>('EXTERNAL_URL')

    // store
    const drawerNavigationStore = useDrawerNavigationStore()
    const userStore = useUserStore()

    const body: Ref<EventTarget | null> = ref(null)
    const frame: Ref<EventTarget | null> = ref(null)
    const background: Ref<EventTarget[]> = ref([])

    const followService = ref({
      melmaga: {
        to: newUserSessionPath(),
        isTargetBlank: false,
      },
      facebook: {
        to: EXTERNAL_URL!.bizhint_facebook_url,
        isTargetBlank: true,
      },
      twitter: {
        to: EXTERNAL_URL!.bizhint_twitter_url,
        isTargetBlank: true,
      },
    })

    const userNavigation = computed(() => [
      {
        label: 'タイムライン',
        to: timelinePath(),
      },
      {
        label: '設定',
        to: editUserProfilesPath(),
      },
    ])

    const globalNavigation = computed<DrawernavigationItem[]>(() => {
      const themeNavigations: GlobalNavigationItem[] = props.items.filter(v => v.priority != null).map(v => {
        return {
          label: v.title,
          to: themePath({ id: v.id }),
        }
      })
      const [topThemeItem, ...restThemeItems] = themeNavigations

      const menuItems = [
        ...(topThemeItem ? [topThemeItem] : []),

        {
          label: 'お役立ち資料',
          to: documentsPath(),
        },
        {
          label: 'ノウハウ動画',
          to: videosPath(),
        },
        {
          label: 'イベント・セミナー',
          to: eventsPath(),
        },

        ...restThemeItems,

        {
          label: 'ランキング',
          to: rankingIndexPath(),
        },
      ]

      return [
        {
          header: 'メニュー',
          items: menuItems,
        },
        {
          header: 'BizHint について',
          items: [
            {
              label: 'BizHint とは',
              to: staticPagesAboutPath(),
            },
            {
              label: 'ヘルプ・お問い合わせ',
              to: staticPagesHelpPath(),
            },
            {
              label: 'お知らせ',
              to: pressReleasesPath(),
            },
            {
              label: 'ご利用規約',
              to: staticPagesTermsPath(),
            },
            {
              label: 'プライバシーポリシー',
              to: staticPagesPrivacyPath(),
            },
            {
              label: '運営会社',
              to: EXTERNAL_URL!.bizhint_corporate_url || '',
              target: '_blank',
            },
            {
              label: '広告掲載に関する資料請求',
              to: EXTERNAL_URL!.ad_document_request_url || '',
              target: '_blank',
            },
            {
              label: '取材のお問い合わせ',
              to: EXTERNAL_URL!.contact_for_interview_url || '',
              target: '_blank',
            },
          ],
        },
        {
          header: 'カテゴリ',
          items: props.keywords,
        },
      ]
    })

    onMounted(() => {
      if (body.value) background.value.push(body.value)
      if (frame.value) background.value.push(frame.value)
    })

    /**
     * ナビゲーション部分以外の場所がクリックされたら閉じる
     * @param event
     */
    const backDrop = (event: Event) => {
      if (event.target && background.value.includes(event.target)) {
        drawerNavigationStore.hideDrawerNavigation()
      }
    }

    return {
      // routes
      mypagePath,
      newUserSessionPath,
      newUserRegistrationPath,
      destroyUserSessionPath,

      // store
      drawerNavigationStore,
      userStore,

      // data
      body,
      frame,
      followService,

      // computed
      userNavigation,
      globalNavigation,

      // methods
      backDrop,
    }
  },
})
</script>

<template>
  <transition name="is-slide__full__horizontal">
    <nav
      v-show="drawerNavigationStore.context.isShow"
      ref="frame"
      :class="[
        'f-navigation__global',
        { 'is-active': drawerNavigationStore.context.isShow },
      ]"
      @click="backDrop">
      <div
        ref="body"
        class="f-navigation__global--body">
        <div class="l-unit__globalNavigation">
          <div class="l-unit__globalNavigation--header">
            <div
              :class="[
                'o-heading__globalNavigation',
                { 'is-signIn': userStore.context.isSignedIn },
              ]">
              <a
                v-if="userStore.context.isSignedIn"
                class="o-heading__globalNavigation--item"
                :href="mypagePath()">
                <span class="o-heading__globalNavigation--label is-primary">
                  {{ userStore.context.displayName }}
                </span>
                <span class="o-heading__globalNavigation--label is-secondary">
                  マイページを見る
                </span>
                <span class="o-heading__globalNavigation--icon is-avatar">
                  <img
                    class="img"
                    :src="userStore.context.thumbImageUrl"
                    alt="">
                </span>
              </a>

              <a
                v-else
                class="o-heading__globalNavigation--item"
                :href="newUserSessionPath()">
                会員の方はこちらからログイン
              </a>
            <!-- /.o-heading__globalNavigation --></div>
          <!-- /.l-unit__globalNavigation header --></div>

          <div class="l-unit__globalNavigation--description">
            <div class="o-list__navigationCategory">
              <dl
                v-if="userStore.context.isSignedIn"
                class="o-list__navigationCategory--unit">
                <dd class="o-list__navigationCategory--description">
                  <div class="o-list__globalNavigation">
                    <ul class="o-list__globalNavigation--items">
                      <template
                        v-for="item in userNavigation"
                        :key="item.to">
                        <li class="o-list__globalNavigation--item">
                          <div class="o-action__globalNavigation">
                            <a
                              class="o-action__globalNavigation--item"
                              :href="item.to"
                              :data-ga-action="item.label">
                              {{ item.label }}
                            </a>
                          <!-- /.o-action__globalNavigation --></div>
                        </li>
                      </template>
                    </ul>
                  <!-- /.o-list__globalNavigation --></div>
                </dd>
              </dl>
              <dl
                v-else
                class="o-list__navigationCategory--unit">
                <dd class="o-list__navigationCategory--description">
                  <p class="p u-text__center u-text__x-small">
                    はじめての方はご登録ください（無料）
                  </p>

                  <ButtonSubmit
                    :href="newUserRegistrationPath()"
                    :is-font-small="true">
                    無料登録
                  </ButtonSubmit>
                </dd>
              </dl>
              <dl
                v-for="(navigation, i) in globalNavigation"
                :key="i"
                class="o-list__navigationCategory--unit">
                <dt
                  v-if="navigation.header"
                  class="o-list__navigationCategory--term">
                  {{ navigation.header }}
                </dt>

                <dd class="o-list__navigationCategory--description">
                  <div class="o-list__globalNavigation">
                    <ul class="o-list__globalNavigation--items">
                      <template
                        v-for="item in navigation.items"
                        :key="item.to">
                        <li
                          v-if="!item.isSignInOnly || userStore.context.isSignedIn"
                          class="o-list__globalNavigation--item">
                          <div class="o-action__globalNavigation">
                            <a
                              class="o-action__globalNavigation--item"
                              :href="item.to"
                              :data-ga-action="item.label"
                              :target="item.target || undefined">
                              {{ item.label }}
                            </a>
                          <!-- /.o-action__globalNavigation --></div>
                        </li>
                      </template>
                    </ul>
                  <!-- /.o-list__globalNavigation --></div>
                </dd>
              </dl>
            <!-- /.o-list__navigationCategory --></div>
          <!-- /.l-unit__globalNavigation description --></div>

          <div
            v-if="userStore.context.isSignedIn"
            class="l-unit__globalNavigation--action is-signOut">
            <FormDefault
              :action="destroyUserSessionPath()"
              method="post"
              name="sign_out">
              <!--
                delete メソッドで Form submit する
                @see https://portaltan.hatenablog.com/entry/2015/07/22/122031
              -->
              <input
                class="input"
                type="hidden"
                name="_method"
                value="DELETE">
              <div class="o-action__signOut">
                <button
                  class="o-action__signOut--item"
                  type="submit">
                  ログアウト
                </button>
              <!-- /.o-action__signOut --></div>
            </FormDefault>
          <!-- /.l-unit__globalNavigation action --></div>

          <div
            :class="[
              'l-unit__globalNavigation--action',
              'is-close',
              { 'is-active': drawerNavigationStore.context.isShow },
            ]">
            <div class="o-action__close">
              <button
                class="o-action__close--item"
                @click="drawerNavigationStore.hideDrawerNavigation">
                閉じる
              </button>
            <!-- /.o-action__close --></div>
          <!-- /.l-unit__globalNavigation action --></div>

          <div class="l-unit__globalNavigation--footer is-followSns">
            <div class="o-list__followSns">
              <dl class="o-list__followSns--unit">
                <dt class="o-list__followSns--term">
                  最新情報はニュースレター・SNSで配信中
                </dt>
                <dd
                  v-for="(item, key) in followService"
                  :key="key"
                  class="o-list__followSns--description">
                  <div class="o-action__cta">
                    <a
                      class="o-action__cta--item"
                      :href="item.to"
                      :target="item.isTargetBlank ? '_blank' : '_self'">
                      <i
                        v-if="key === 'melmaga'"
                        :class="[
                          `i-melmaga`,
                          'is-large',
                        ]" />
                      <i
                        v-else
                        :class="[
                          `i-sns__${key}`,
                          'is-large',
                        ]" />
                    </a>
                  <!-- /.o-action__cta --></div>
                </dd>
              </dl>
            <!-- /.o-list__followSns --></div>
          <!-- /.l-unit__globalNavigation footer --></div>
        <!-- /.l-unit__globalNavigation --></div>
      <!-- /.f-navigation__global body --></div>
    <!-- /.f-navigation__global --></nav>
  </transition>
</template>

<style lang="sass" scoped>
// foundation
.f-navigation__global
  $root: &
  width: 100%
  height: 100%
  position: fixed
  top: 0
  left: 0
  z-index: 20

  & &--body
    width: 100%
    height: 100%
    display: flex
    justify-content: flex-end
    overflow-y: auto

// layout
.l-unit__globalNavigation
  $root: &
  width: 100%
  min-height: 100%
  max-width: #{$global-navigation-width-sp}px
  display: flex
  flex-direction: column
  position: relative

  & &--header
    background-color: rgba($bg-x-light-orange, 1)

  & &--description
    background-color: rgba($bg-white, 1)

  & &--action
    &.is-signOut
      order: 1
      padding: 1em 1em 5em
      background-color: rgba($bg-white, 1)
      border-top: 1px solid $border-x-light-gray

    &.is-close
      position: fixed
      top: 0
      right: #{$global-navigation-width-sp}px

      &.is-active
        animation:
          name: slideInCloseButton
          duration: 1s  // double the drawer slide-in transition time.
          fill-mode: backwards
          timing-function: ease

      @keyframes slideInCloseButton
        0%
          transform: scale(0.7) translateX(15px)
          opacity: 0

        50%
          transform: scale(0.7) translateX(15px)
          opacity: 0

        100%
          transform: scale(1) translateX(0)
          opacity: 1

  & &--footer
    &.is-followSns
      padding: 0.7em 0 1em
      background-color: rgba($bg-white, 1)
      border-top: 1px solid $border-x-light-gray

// object
.o-heading__globalNavigation
  $root: &
  $icon-size: 6px
  $font-normal-px: 16
  $avatar-size: 40
  $margin-size: 8
  $padding-size: 16
  $padding-em: math.div($padding-size, $font-normal-px)
  $padding-left: math.div(($padding-size + $avatar-size + $margin-size), $font-normal-px)

  & &--item
    display: block
    position: relative
    color: $font-black
    font-size: $font-small
    text-decoration: none
    padding: #{$padding-em}em

    &::before
      content: ""
      width: $icon-size
      height: $icon-size
      display: block
      position: absolute
      top: 50%
      right: 1.2em
      transform: translateY(-50%) rotate(-45deg)
      border:
        bottom: 0.15em solid $border-dark-orange
        right: 0.15em solid $border-dark-orange

  &.is-signIn
    #{$root}--item
      $icon-size: 4px
      min-height: #{$avatar-size}px
      display: flex
      flex-direction: column
      justify-content: center
      font-size: $font-normal
      padding: #{$padding-em}em #{$padding-em}em #{$padding-em}em #{$padding-left}em
      box-sizing: content-box

  & &--label
    line-height: 1
    display: block

    &.is-primary
      font:
        size: $font-small
        weight: bold

    &.is-secondary
      font-size: $font-x-small
      margin-top: 0.67em

  & &--icon
    &.is-avatar
      width: #{$avatar-size}px
      height: #{$avatar-size}px
      position: absolute
      top: 50%
      left: 1em
      transform: translateY(-50%)
      overflow: hidden
      border-radius: 50%

.o-list__navigationCategory
  & &--unit
    border-top: 1px solid $border-x-light-gray

    &:first-child
      border-top: none

  & &--term
    line-height: 1
    font:
      size: $font-x-small
      weight: bold
    padding: 0.67em 1.3em
    background-color: rgba($bg-x-light-gray, 1)

  & &--description
    padding: 1em

    > .o-list__globalNavigation
      margin: -0.5em -1em

.o-list__followSns
  & &--unit
    display: flex
    flex-wrap: wrap
    justify-content: center
    align-items: center

  & &--term
    order: 1
    line-height: 1
    font-size: $font-x-small
    margin-top: 1em

  $icon-size-px: 22
  & &--description
    width: changeSizePx2Em($icon-size-px)
    margin-right: 1.5em

    &:last-child
      margin-right: 0

.o-action__cta
  & &--item
    display: block
    line-height: 1
    +transition

    +mouseover
      opacity: 0.6

    > .img
      height: 100%

.o-action__globalNavigation
  $root: &
  & &--item
    display: block
    position: relative
    line-height: 1
    color: $font-xx-dark-gray
    font-size: $font-small
    text-decoration: none
    padding: 0.86em 1.14em

.o-action__signOut
  & &--item
    width: 100%
    line-height: 2.7
    display: block
    color: $font-gray
    font-size: $font-small
    text-align: center
    text-decoration: none
    border: 1px solid $border-x-light-gray
    border-radius: 100vh

.o-action__close
  $close-button-size: 56
  & &--item
    +iconCross($color: $bg-white)
    width: #{$close-button-size}px
    height: #{$close-button-size}px
</style>
